
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { supabase } from "../lib/supabaseClient";
import { formatearNumeroCL, formatoMiles, desformatearMiles } from '../utils/formatUtils';
import { normalizeDateForStorage, getTodayDateString } from '../utils/dateUtils';
import { showError, showSuccess } from '../lib/swal';
import Tooltip from './Tooltip';
import BotonCargando from './BotonCargando';
import { rendimientoSegunColor, usaRendimientoColor, esColorNoNegro, obtenerRebajaPorTipo } from '../utils/rendimientoUtils';

// Colores que puede tener una máquina. En las monocromo siempre es Black.
const COLORES = [
  { valor: 'Black',   emoji: '⚫', activo: 'border-gray-800 bg-gray-800 text-white',        etiqueta: 'bg-gray-800 text-white' },
  { valor: 'Cyan',    emoji: '🔵', activo: 'border-cyan-500 bg-cyan-50 text-cyan-800',      etiqueta: 'bg-cyan-100 text-cyan-800' },
  { valor: 'Magenta', emoji: '🔴', activo: 'border-pink-500 bg-pink-50 text-pink-800',      etiqueta: 'bg-pink-100 text-pink-800' },
  { valor: 'Yellow',  emoji: '🟡', activo: 'border-yellow-500 bg-yellow-50 text-yellow-800', etiqueta: 'bg-yellow-100 text-yellow-800' },
];

const claseEtiquetaColor = (color) =>
  COLORES.find((c) => c.valor === color)?.etiqueta || 'bg-gray-100 text-gray-700';

// Identificador del grupo: une los tóners cambiados en la misma visita.
// crypto.randomUUID no existe en navegadores antiguos, por eso el respaldo.
const nuevoGrupoId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

export default function RegistroForm({ editMode = false, initialData = null, onSave = null, onClose = null }) {
  const [serieInput, setSerieInput] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [maquinasFiltradas, setMaquinasFiltradas] = useState([]);
  const [serieSeleccionada, setSerieSeleccionada] = useState('');
  const [datosMaquina, setDatosMaquina] = useState(null);
  const [tiposToner, setTiposToner] = useState([]);
  const [tipoTonerInstalado, setTipoTonerInstalado] = useState('');
  const [esMaquinaColor, setEsMaquinaColor] = useState(false);

  // Tóners marcados para este cambio. En una máquina monocromo es siempre
  // ['Black']; en una de color, el usuario marca los que cambió.
  const [coloresElegidos, setColoresElegidos] = useState([]);
  // Tóner que usa cada color: { Black: { id, modelo, rendimiento, rendimiento_color } }
  const [tonerPorColor, setTonerPorColor] = useState({});
  // Último cambio de cada color: { Cyan: { contador, fecha, tipoAnterior } }
  // Si la clave no existe, todavía se está consultando.
  const [ultimoPorColor, setUltimoPorColor] = useState({});

  const [contadorActual, setContadorActual] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [fecha, setFecha] = useState(getTodayDateString());
  const [mensajeDuracion, setMensajeDuracion] = useState('');

  const user = useAuthStore(state => state.user);

  // Máquina que se está mostrando: sirve para descartar respuestas que
  // llegan tarde cuando el usuario ya cambió de serie.
  const serieRef = useRef('');
  // Consultas del último contador que están en curso (para no repetirlas)
  const enCurso = useRef(new Set());

  const contadorNumerico = parseInt(contadorActual) || 0;

  // ---- Datos de cada tóner marcado (se calculan en cada render) ----
  //   1) Rendimiento del tóner según el color (negro o color)
  //   2) Menos la rebaja por el tipo de tóner del cambio ANTERIOR de ese color
  //   3) Duración real = contador actual − último contador de ese color
  const infoColores = useMemo(() => {
    return coloresElegidos.map((color) => {
      const toner = tonerPorColor[color] || null;
      const ultimo = ultimoPorColor[color];
      const cargando = ultimo === undefined;
      const base = rendimientoSegunColor(toner, color) || null;
      const { rebaja, nombre: nombreTipoAnterior } = obtenerRebajaPorTipo(ultimo?.tipoAnterior, tiposToner);
      const esperado = base ? Math.round(base * (1 - rebaja)) : null;
      const ultimoContador = cargando ? null : (ultimo?.contador ?? 0);
      const duracion =
        contadorActual !== '' && ultimoContador !== null && contadorNumerico > ultimoContador
          ? contadorNumerico - ultimoContador
          : 0;
      return {
        color,
        toner,
        base,
        rebaja,
        nombreTipoAnterior,
        esperado,
        ultimoContador,
        fechaUltimoCambio: ultimo?.fecha || null,
        duracion,
        cargando,
        duroMenos: !!(esperado && esperado > 0 && duracion > 0 && duracion < esperado),
      };
    });
  }, [coloresElegidos, tonerPorColor, ultimoPorColor, tiposToner, contadorActual, contadorNumerico]);

  const faltanDatos = infoColores.some((i) => i.cargando);
  const coloresQueDuraronMenos = infoColores.filter((i) => i.duroMenos).map((i) => i.color);

  // Preload data for edit mode
  useEffect(() => {
    if (editMode && initialData) {
      setFecha(initialData.fecha ? new Date(initialData.fecha).toISOString().split('T')[0] : getTodayDateString());
      setSerieSeleccionada(initialData.serie_maquina || '');
      setSerieInput(initialData.serie_maquina || '');
      setContadorActual(initialData.contador_actual ? initialData.contador_actual.toString() : '');
      setObservaciones(initialData.observaciones || '');
      setColoresElegidos(initialData.color ? [initialData.color] : []);
      // Soporta tanto el id directo como el objeto anidado desde el join
      setTipoTonerInstalado(
        initialData.tipo_toner_instalado_id ??
        initialData.tipos_toner_instalado?.id ??
        ''
      );
    }
  }, [editMode, initialData]);

  useEffect(() => {
    const fetchDatos = async () => {
      const { data: tipos } = await supabase.from('tipos_toner').select('*');
      setTiposToner(tipos || []);
    };
    fetchDatos();
  }, []);

  useEffect(() => {
    if (!serieInput) return;
    const fetchFiltrado = async () => {
      const { data } = await supabase
        .from('maquinas')
        .select('id, serie')
        .ilike('serie', `%${serieInput}%`);
      setMaquinasFiltradas(data || []);
    };
    fetchFiltrado();
  }, [serieInput]);

  // 1) Al elegir una serie: cargar los datos de la máquina.
  useEffect(() => {
    // Limpiar lo que venga de la máquina anterior
    serieRef.current = serieSeleccionada;
    setTonerPorColor({});
    setUltimoPorColor({});

    if (!serieSeleccionada) {
      setDatosMaquina(null);
      setEsMaquinaColor(false);
      setColoresElegidos([]);
      return;
    }

    let cancelado = false;

    const fetchMaquina = async () => {
      const { data: maquina } = await supabase
        .from('maquinas')
        .select(`
          id, modelo, cliente_id, modelo_toner_id, tipo_toner_id,
          departamento, serie, es_color, contador_inicial,
          clientes ( nombre, ubicacion )
        `)
        .eq('serie', serieSeleccionada)
        .single();

      if (cancelado || !maquina) return;

      setDatosMaquina({
        ...maquina,
        cliente: maquina.clientes?.nombre || '',
        ubicacion: maquina.clientes?.ubicacion || '',
      });

      // El tipo sugerido es el de ESTA máquina (antes quedaba pegado el
      // de la máquina elegida anteriormente).
      setTipoTonerInstalado(prev => (editMode && prev ? prev : maquina.tipo_toner_id));
      setEsMaquinaColor(!!maquina.es_color);
      // Monocromo: siempre negro. Color: el usuario marca los que cambió.
      setColoresElegidos(prev => {
        if (!maquina.es_color) return ['Black'];
        return editMode ? prev : [];
      });
    };

    fetchMaquina();
    return () => { cancelado = true; };
  }, [serieSeleccionada]);

  // 2) Con la máquina cargada: qué tóner usa cada color.
  //    Una sola consulta para toda la máquina (no una por color).
  useEffect(() => {
    if (!datosMaquina?.id) return;

    let cancelado = false;

    const cargarToners = async () => {
      const tonerIdPorColor = {};

      if (datosMaquina.es_color) {
        const { data: relaciones } = await supabase
          .from('toner_por_maquina')
          .select('color, modelo_toner_id')
          .eq('maquina_id', datosMaquina.id);

        (relaciones || []).forEach((r) => {
          if (r.color && r.modelo_toner_id) tonerIdPorColor[r.color] = r.modelo_toner_id;
        });
      }

      // El color que no tenga tóner propio usa el general de la máquina
      COLORES.forEach((c) => {
        if (!tonerIdPorColor[c.valor] && datosMaquina.modelo_toner_id) {
          tonerIdPorColor[c.valor] = datosMaquina.modelo_toner_id;
        }
      });

      const ids = [...new Set(Object.values(tonerIdPorColor))];
      let toners = [];
      if (ids.length) {
        const { data } = await supabase
          .from('toners')
          .select('id, modelo, rendimiento, rendimiento_color')
          .in('id', ids);
        toners = data || [];
      }

      if (cancelado) return;

      const porId = Object.fromEntries(toners.map((t) => [t.id, t]));
      const porColor = {};
      Object.entries(tonerIdPorColor).forEach(([color, id]) => {
        if (porId[id]) porColor[color] = porId[id];
      });
      setTonerPorColor(porColor);
    };

    cargarToners();
    return () => { cancelado = true; };
  }, [datosMaquina]);

  // 3) Último cambio de cada color marcado. Solo se consulta el color que
  //    falta, y una sola vez: si se desmarca y se vuelve a marcar, ya está.
  useEffect(() => {
    if (!datosMaquina?.serie) return;

    const serie = datosMaquina.serie;
    const maquinaEsColor = !!datosMaquina.es_color;

    coloresElegidos.forEach(async (color) => {
      if (ultimoPorColor[color] !== undefined) return;
      const clave = `${serie}|${color}`;
      if (enCurso.current.has(clave)) return;
      enCurso.current.add(clave);

      let consulta = supabase
        .from('registros')
        .select('contador_actual, fecha, tipo_toner_instalado_id')
        .eq('serie_maquina', serie);
      if (maquinaEsColor) consulta = consulta.eq('color', color);

      const { data } = await consulta
        .order('fecha', { ascending: false })
        .order('id', { ascending: false })
        .limit(1);

      enCurso.current.delete(clave);
      // Si mientras tanto se cambió de máquina, esta respuesta ya no sirve
      if (serieRef.current !== serie) return;

      const ultimo = data?.[0] || null;
      setUltimoPorColor((prev) => ({
        ...prev,
        [color]: {
          // Sin registros previos: se parte del contador inicial de la máquina
          contador: ultimo ? (ultimo.contador_actual || 0) : (datosMaquina.contador_inicial || 0),
          fecha: ultimo?.fecha || null,
          // La rebaja se calcula con el tipo del cambio ANTERIOR, no el actual
          tipoAnterior: ultimo?.tipo_toner_instalado_id || datosMaquina.tipo_toner_id || null,
        },
      }));
    });
  }, [coloresElegidos, datosMaquina, ultimoPorColor]);

  const alternarColor = (color) => {
    setColoresElegidos((prev) =>
      prev.includes(color) ? prev.filter((c) => c !== color) : [...prev, color]
    );
  };

  // Observación obligatoria: siempre para secretarias, o si algún tóner
  // duró menos de lo esperado.
  const esSecretaria = user?.role === 'secretaria';
  const requiereObservacion = esSecretaria || coloresQueDuraronMenos.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();

    const colores = esMaquinaColor ? coloresElegidos : ['Black'];

    if (!colores.length) {
      showError('Falta el tóner', 'Marca al menos un tóner de los que cambiaste.');
      return;
    }

    if (faltanDatos) {
      showError('Un momento', 'Todavía se están cargando los datos de los tóners marcados.');
      return;
    }

    const contadorNum = parseInt(contadorActual);
    const mayorUltimo = Math.max(...infoColores.map((i) => i.ultimoContador ?? 0), 0);

    // In edit mode, don't validate contador against ultimoContador since we're editing existing data
    if (!editMode && contadorNum < mayorUltimo) {
      showError(
        'Contador inválido',
        `El contador actual no puede ser menor que el último registrado (${formatearNumeroCL(mayorUltimo)}).`
      );
      return;
    }

    if (requiereObservacion && !observaciones.trim()) {
      if (coloresQueDuraronMenos.length === 1) {
        const i = infoColores.find((x) => x.duroMenos);
        showError(
          'Observación obligatoria',
          `El tóner ${i.color} duró ${formatearNumeroCL(i.duracion)} copias, pero se esperaban ${formatearNumeroCL(i.esperado)}. Explica por qué duró menos de lo esperado.`
        );
      } else if (coloresQueDuraronMenos.length > 1) {
        showError(
          'Observación obligatoria',
          `Estos tóners duraron menos de lo esperado: ${coloresQueDuraronMenos.join(', ')}. Explica el motivo.`
        );
      } else {
        showError(
          'Observación obligatoria',
          'Debes ingresar una descripción del cambio de tóner.'
        );
      }
      return;
    }

    // Ajustar fecha para zona horaria GMT-4 (Chile)
    const [year, month, day] = fecha.split('-').map(Number);
    const localDate = new Date(year, month - 1, day + 1);
    // Agregar 4 horas para compensar GMT-4
    localDate.setHours(localDate.getHours() + 4);
    const adjustedDate = localDate.toISOString().split('T')[0];

    setGuardando(true);
    try {
      if (editMode && onSave) {
        // For edit, call onSave with updated data
        const color = colores[0];
        const info = infoColores.find((i) => i.color === color);
        const updatedData = {
          id: initialData.id,
          fecha: adjustedDate,
          serie_maquina: serieSeleccionada,
          contador_actual: contadorNum,
          observaciones,
          color: esMaquinaColor ? color : 'Black',
          tipo_toner_instalado_id: tipoTonerInstalado,
          // Include machine data for update
          cliente_id: datosMaquina?.cliente_id,
          modelo_maquina: datosMaquina?.modelo,
          modelo_toner_id: info?.toner?.id || datosMaquina?.modelo_toner_id,
          ultimo_contador: info?.ultimoContador ?? null,
        };
        onSave(updatedData);
      } else {
        // Un registro por cada tóner cambiado, todos con el mismo grupo:
        // así cada color conserva su contador y su duración, pero el
        // sistema los muestra, aprueba y despacha como un solo cambio.
        const grupoId = nuevoGrupoId();
        const filas = colores.map((color) => {
          const info = infoColores.find((i) => i.color === color);
          return {
            fecha: adjustedDate, // Fecha ajustada para GMT-4
            cliente_id: datosMaquina.cliente_id,
            modelo_maquina: datosMaquina.modelo,
            serie_maquina: serieSeleccionada,
            // El tóner del color que se cambió (si el color no tiene uno
            // propio, es el general de la máquina)
            modelo_toner_id: info?.toner?.id || datosMaquina.modelo_toner_id,
            tipo_toner_instalado_id: tipoTonerInstalado,
            color,
            ultimo_contador: info?.ultimoContador ?? 0,
            contador_actual: contadorNum,
            observaciones,
            creado_por: user?.id,
            grupo_id: grupoId,
          };
        });

        const { error } = await supabase.from('registros_pendientes').insert(filas);

        if (!error) {
          showSuccess(
            filas.length > 1 ? 'Registro guardado' : 'Registro guardado',
            filas.length > 1
              ? `Se guardaron ${filas.length} tóners (${colores.join(', ')}) como un solo cambio.`
              : 'El registro se guardó correctamente.'
          );

          setSerieSeleccionada('');
          setSerieInput('');
          setContadorActual('');
          setFecha(getTodayDateString());
          setObservaciones('');
          setDatosMaquina(null);
          setColoresElegidos([]);
          setTonerPorColor({});
          setUltimoPorColor({});
        } else {
          showError(
            'No se pudo guardar el registro',
            error.message?.includes('grupo_id')
              ? 'Falta ejecutar el script 16 en la base de datos (columna grupo_id).'
              : 'Revisa tu conexión e intenta nuevamente.'
          );
        }
      }
    } finally {
      setGuardando(false);
    }
  };

  const formularioInvalido =
    !fecha ||
    !serieSeleccionada ||
    contadorActual === '' ||
    coloresElegidos.length === 0 ||
    faltanDatos ||
    (requiereObservacion && !observaciones.trim());

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl max-w-4xl mx-auto border border-gray-100 overflow-hidden animate-fadeIn">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white bg-opacity-20 rounded-xl">
            <svg className="w-8 h-8 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h2 className="text-2xl lg:text-3xl font-bold text-white">
              {editMode ? 'Editar Registro de Tóner' : 'Nuevo Registro de Tóner'}
            </h2>
            <p className="text-indigo-100 text-sm mt-1">
              Complete la información del cambio de tóner
            </p>
          </div>
        </div>
      </div>

      {/* Form Body */}
      <div className="p-6 lg:p-8 space-y-6">
        {/* Fecha */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Fecha del cambio <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200 bg-white"
            required
          />
        </div>

        {/* Serie - Autocomplete */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Buscar número de serie <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              value={serieInput}
              onChange={(e) => {
                setSerieInput(e.target.value);
                setSerieSeleccionada('');
              }}
              placeholder="Escriba el número de serie..."
              className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200 bg-white"
            />
            {serieSeleccionada && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
          </div>
          {serieInput && maquinasFiltradas.length > 0 && (
            <div className="mt-2 border-2 border-gray-200 rounded-xl overflow-hidden shadow-lg max-h-48 overflow-y-auto">
              {maquinasFiltradas.map((m) => (
                <div
                  key={m.id}
                  className="px-4 py-3 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 cursor-pointer transition-all duration-200 border-b border-gray-100 last:border-b-0"
                  onClick={() => {
                    setSerieSeleccionada(m.serie);
                    setSerieInput(m.serie);
                    setMaquinasFiltradas([]);
                  }}
                >
                  <span className="font-mono font-semibold text-gray-900">{m.serie}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info de máquina */}
        {datosMaquina && (
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 border-2 border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900">Información de la Máquina</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Cliente</p>
                <p className="text-base font-semibold text-gray-900">{datosMaquina.cliente}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Ubicación</p>
                <p className="text-base font-semibold text-gray-900">{datosMaquina.ubicacion}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Departamento</p>
                <p className="text-base font-semibold text-gray-900">{datosMaquina.departamento}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-200">
                <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Modelo</p>
                <p className="text-base font-semibold text-gray-900">{datosMaquina.modelo}</p>
              </div>
            </div>

            {/* Tóners que se cambian */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {esMaquinaColor ? 'Tóners que se cambian' : 'Color del tóner a cambiar'}
                {esMaquinaColor && <span className="text-red-500"> *</span>}
                {esMaquinaColor && (
                  <span className="ml-1 align-middle inline-flex">
                    <Tooltip texto="Marca todos los tóners que cambiaste en esta máquina. Quedan como un solo cambio: se aprueban juntos y llevan una sola guía de despacho." />
                  </span>
                )}
              </label>

              {esMaquinaColor ? (
                <>
                  <div className="flex flex-wrap gap-2">
                    {COLORES.map((c) => {
                      const marcado = coloresElegidos.includes(c.valor);
                      return (
                        <button
                          key={c.valor}
                          type="button"
                          aria-pressed={marcado}
                          onClick={() => alternarColor(c.valor)}
                          className={`px-4 py-2.5 rounded-xl border-2 font-semibold text-sm transition-all duration-200 ${
                            marcado ? c.activo : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          <span className="mr-1.5">{c.emoji}</span>
                          {c.valor}
                          {marcado && <span className="ml-2">✓</span>}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {coloresElegidos.length > 1
                      ? `${coloresElegidos.length} tóners en un solo cambio: se aprueban juntos y llevan una sola guía.`
                      : 'Puedes marcar varios si cambiaste más de un tóner en esta máquina.'}
                  </p>
                </>
              ) : (
                <input
                  type="text"
                  value="Black"
                  readOnly
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-100 text-gray-700 cursor-not-allowed"
                />
              )}
            </div>

            <div className="grid grid-cols-1 gap-4">
              {/* Un bloque por cada tóner marcado */}
              {infoColores.map((info) => (
                <div key={info.color} className="bg-white rounded-xl p-4 border border-gray-200">
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full ${claseEtiquetaColor(info.color)}`}>
                        {info.color}
                      </span>
                      <span className="text-sm font-semibold text-gray-900">
                        {info.toner?.modelo || (info.cargando ? 'Cargando…' : 'Sin tóner asignado')}
                      </span>
                    </div>
                    {info.fechaUltimoCambio ? (
                      <span className="text-xs text-gray-500">
                        Último cambio: {new Date(info.fechaUltimoCambio).toLocaleDateString('es-CL')}
                      </span>
                    ) : !info.cargando && (
                      <span className="text-xs font-medium text-green-600">Primer registro de este tóner</span>
                    )}
                  </div>

                  <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
                    <div>
                      <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Último contador</dt>
                      <dd className="text-base font-bold text-gray-900 tabular-nums">
                        {info.cargando ? '…' : formatearNumeroCL(info.ultimoContador)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Rendimiento esperado</dt>
                      <dd className="text-base font-bold text-green-700 tabular-nums">
                        {info.esperado ? `${formatearNumeroCL(info.esperado)} copias` : '—'}
                      </dd>
                    </div>
                    {info.duracion > 0 && (
                      <div>
                        <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Duración</dt>
                        <dd className={`text-base font-bold tabular-nums ${
                          !info.esperado || info.duracion >= info.esperado
                            ? 'text-indigo-700'
                            : info.duracion >= info.esperado * 0.8
                              ? 'text-amber-600'
                              : 'text-red-600'
                        }`}>
                          {formatearNumeroCL(info.duracion)} copias
                        </dd>
                      </div>
                    )}
                  </dl>

                  {/* Desglose del cálculo, para poder verificarlo */}
                  {info.base && (
                    <p className="text-xs text-gray-500 mt-2">
                      {formatearNumeroCL(info.base)} copias del tóner
                      {' '}
                      {usaRendimientoColor(info.toner, info.color)
                        ? '(rendimiento color)'
                        : esColorNoNegro(info.color)
                          ? '(el tóner no tiene rendimiento de color, se usa el general)'
                          : '(rendimiento negro)'}
                      {info.rebaja > 0 && ` − ${Math.round(info.rebaja * 100)}% por tóner ${info.nombreTipoAnterior}`}
                    </p>
                  )}

                  {/* Aviso cuando duró menos de lo esperado */}
                  {info.duracion > 0 && info.esperado > 0 && (
                    <div className={`mt-3 rounded-lg px-3 py-2 text-sm font-semibold ${
                      info.duracion >= info.esperado
                        ? 'bg-green-50 text-green-700'
                        : info.duracion >= info.esperado * 0.8
                          ? 'bg-amber-50 text-amber-700'
                          : 'bg-red-50 text-red-700'
                    }`}>
                      {info.duracion >= info.esperado
                        ? 'Duró lo esperado o más'
                        : info.duracion >= info.esperado * 0.8
                          ? 'Cerca del rendimiento esperado'
                          : 'Duró menos de lo esperado'}
                    </div>
                  )}
                </div>
              ))}

              {esMaquinaColor && coloresElegidos.length === 0 && (
                <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
                  <p className="text-sm font-medium text-amber-700">
                    Marca arriba el o los tóners que cambiaste en esta máquina.
                  </p>
                </div>
              )}

              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-4 border-2 border-blue-200">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-blue-700 mb-1 uppercase tracking-wide">Tipo Recomendado</p>
                    <p className="text-base font-bold text-blue-900">
                      {tiposToner.find(t => t.id === datosMaquina.tipo_toner_id)?.nombre || 'No definido'}
                    </p>
                    <p className="text-xs text-blue-600 mt-2">
                      💡 El administrador seleccionará el tipo de tóner a despachar durante la aprobación
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contador actual */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Contador actual <span className="text-red-500">*</span>
            <span className="ml-1 align-middle inline-flex"><Tooltip texto="Es la lectura del contador de copias que muestra la máquina en este momento. Con este número y el anterior de cada tóner, el sistema calcula cuántas copias duró cada uno." /></span>
          </label>
          <input
            type="text"
            value={formatoMiles(contadorActual)}
            onChange={(e) => setContadorActual(desformatearMiles(e.target.value))}
            placeholder="Ingrese el contador actual..."
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200 bg-white font-mono text-lg"
            required
          />
          {coloresElegidos.length > 1 && (
            <p className="text-xs text-gray-500 mt-2">
              Es la lectura de la máquina: se usa para los {coloresElegidos.length} tóners marcados.
            </p>
          )}
        </div>

        {/* Observaciones */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Observaciones
            {requiereObservacion && <span className="text-red-500 ml-1">*</span>}
            {requiereObservacion && <span className="ml-1 align-middle inline-flex"><Tooltip texto="La observación es obligatoria en este caso, por ejemplo cuando el tóner duró menos copias de lo esperado. Explica brevemente el motivo para dejar constancia." /></span>}
          </label>
          <textarea
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200 resize-none ${
              requiereObservacion
                ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-200'
                : 'border-gray-200 bg-white focus:border-indigo-500 focus:ring-indigo-200'
            }`}
            rows={4}
            placeholder={
              coloresQueDuraronMenos.length > 0
                ? "OBLIGATORIO: Explique por qué el tóner duró menos de lo esperado..."
                : esSecretaria
                  ? "OBLIGATORIO: Describa el cambio de tóner (motivo, condiciones, detalles importantes)..."
                  : "Observaciones opcionales (detalles adicionales del cambio)..."
            }
          />
          {requiereObservacion && (
            <div className="mt-2 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
              <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-red-700 text-sm font-medium">
                {coloresQueDuraronMenos.length > 1
                  ? `Observación obligatoria: estos tóners duraron menos de lo esperado (${coloresQueDuraronMenos.join(', ')}). Debe explicar el motivo antes de enviar.`
                  : coloresQueDuraronMenos.length === 1
                    ? `Observación obligatoria: el tóner ${coloresQueDuraronMenos[0]} duró menos de lo esperado. Debe explicar el motivo antes de enviar.`
                    : 'Observación obligatoria: Debe describir el cambio de tóner antes de enviar.'}
              </p>
            </div>
          )}
        </div>

        {/* Advertencia de campos requeridos */}
        {formularioInvalido && (
          <div className="flex items-start gap-2 p-4 bg-amber-50 border-2 border-amber-200 rounded-xl">
            <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-amber-700 text-sm font-medium">
              Complete todos los campos requeridos marcados con <span className="text-red-500">*</span>
            </p>
          </div>
        )}
      </div>

      {/* Footer con botones */}
      <div className="px-4 sm:px-6 lg:px-8 py-6 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row gap-3 justify-end">
        {editMode && onClose && (
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-all duration-200"
          >
            Cancelar
          </button>
        )}
        <BotonCargando
          type="submit"
          cargando={guardando}
          textoCargando={editMode ? 'Actualizando...' : 'Guardando...'}
          disabled={formularioInvalido}
          className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium rounded-xl hover:shadow-lg transform hover:scale-105 active:scale-95 disabled:transform-none disabled:hover:shadow-none"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {editMode
            ? 'Actualizar Registro'
            : coloresElegidos.length > 1
              ? `Guardar ${coloresElegidos.length} Tóners`
              : 'Guardar Registro'}
        </BotonCargando>
      </div>

      {mensajeDuracion && (
        <div className="px-4 sm:px-6 lg:px-8 pb-4">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-blue-700 font-semibold">{mensajeDuracion}</p>
          </div>
        </div>
      )}
    </form>
  );
}
